<template>
    <div v-if="canUpdate">
        <settings-heading
            :title="$t('settings.metadata.title')"
            :description="$t('settings.metadata.description')"
            :button="$t('settings.save')"
            :button-disabled="!form.isDirty()"
            button-icon="add-circle"
            :button-loading="form.submitting"
            @button-click="submitForm"
        />

        <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs" class="w-full">
            <div v-for="lang in languages" :key="lang">
                <simple-input
                    v-show="lang === selectedLanguage"
                    v-model="form.og_title[lang]"
                    :autofocus="lang === selectedLanguage"
                    class="mb-16">
                    {{ $t('form.title') }}
                    <span v-if="languages.length > 1">({{ lang }})</span>
                </simple-input>

                <simple-textarea
                    v-show="lang === selectedLanguage"
                    v-model="form.og_description[lang]"
                    class="mb-32"
                >
                    {{ $t('form.description') }}
                    <span v-if="languages.length > 1">({{ lang }})</span>
                </simple-textarea>

            </div>
        </translatable>

        <div class="w-full mb-32">
            <label class="mb-8 text-body-sm flex items-center">
                <span class="first-letter:uppercase font-semibold text-black">{{ $t('jobs.og_image') }}</span>
            </label>

            <siu
                v-model="form.og_image_path_queued_id"
                :image="imageUrl"
                :minHeight="150"
                :minWidth="150"
                classes="h-240 w-full rounded-6 shadow-md"
                icon-location="corner"
                required
            ></siu>
        </div>
        <div class="flex justify-end mb-48">
            <simple-button
                :disabled="!form.isDirty()"
                display="secondary"
                icon="save"
                :loading="form.submitting"
                look="primary"
                @click="submitForm"
            >
                {{ $t('settings.save') }}
            </simple-button>
        </div>

    </div>
    <error-403 v-else></error-403>
</template>

<script>
import SimpleButton from '../../components/ui/SimpleButton.vue';
import Translatable from '../../components/forms/Translatable.vue';
import Input from '../../components/forms/Input.vue';
import Form from '../../etc/forms/Form';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import * as jobsiteService from '../../services/JobsiteService';
import Jobsite from '../../models/Jobsite';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import {normalizeJson} from '../../../shared/helpers';
import confirmLeave from '../../mixins/confirmLeave';
import Error403 from '../../components/ui/Error403.vue';
import TextArea from '../../components/forms/TextArea.vue';
import SingleImageUpload from '../../components/forms/SingleImageUpload';
import SettingsHeading from "../../components/ui/SettingsHeading.vue";


export default {
    components: {
        SettingsHeading,
        'simple-button': SimpleButton,
        'translatable': Translatable,
        'simple-input': Input,
        'error-403': Error403,
        'simple-textarea': TextArea,
        'siu': SingleImageUpload,
    },
    mixins: [
        confirmLeave,
    ],
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            form: this.generateForm(),
            canUpdate: this.$can('settings.jobsite'),
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        imageUrl: {
            get() {
                return (typeof this.currentCompany.jobsite !== 'undefined') ? this.currentCompany.jobsite.og_image_url : null;
            },
        },
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.og_title[languages[index]]) {
                    errorLang.push(languages[index]);
                }

                if (!this.form.data.og_description[languages[index]] || this.form.data.og_description[languages[index]].match(/<p><\/p>/g)) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        generateForm() {
            return new Form([
                {name: 'og_title', value: this.mainStore.currentCompany.jobsite.og_title},
                {name: 'og_description', value: this.mainStore.currentCompany.jobsite.og_description},
                {name: 'og_image_path', value: 'default'},
                {name: 'og_image_path_queued_id', value: null},
            ]);
        },
        submitForm() {
            if (this.form.data.og_image_path === 'default') {
                delete this.form.data.og_image_path;
            }

            this.form.submitting = false;

            this.form.submit(data => {
                return jobsiteService.update(this.currentCompany.id, {
                    data: {attributes: data},
                });
            }).then(result => {
                return useRepo(Jobsite)
                    .whereId(result.id)
                    .update(normalizeJson(result));
            }).then(() => {
                this.form = this.generateForm();
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('settings.saved', {subject: this.$t('settings.metadata.title')}),
                });
            });

            this.form.submitting = false;
        },
        refetchData() {
            jobsiteService.show(this.currentCompany.id).then(result => {
                return useRepo(Jobsite).save(normalizeJson(result.data));
            }).then(result => {
                if (this.form.isDifferent(result)) {
                    this.form = this.generateForm();
                }
            });
        },
    },
    created() {
        this.refetchData();
    },
};
</script>
